import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import DateRangePicker, { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import { apiService } from '../../services/ApiService';
import { AnalyticsResponse, ModelConfig, ModelMetricsAggregated, labelModel } from '../../types';
import { TEST_SCENARIOS } from '../../config';
import { TokenUsageChart } from './TokenUsageChart';
import { ResponseTimeChart } from './ResponseTimeChart';
import { RatingHeatmap } from './RatingHeatmap';
import { SuccessRateChart } from './SuccessRateChart';
import { SummaryCards } from './SummaryCards';

const scenarioOptions: SelectProps.Option[] = [
  { label: 'All Scenarios', value: 'all' },
  ...TEST_SCENARIOS.map((s) => ({
    label: `Test ${s.id}: ${s.name}`,
    value: String(s.id),
  })),
];

const RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
  { key: 'last-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'last-30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: 'last-90-days', amount: 90, unit: 'day', type: 'relative' },
];

function resolveDateRange(value: DateRangePickerProps.Value | null): { startDate?: string; endDate?: string } {
  if (!value) return {};
  if (value.type === 'absolute') {
    return { startDate: value.startDate, endDate: value.endDate };
  }
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const start = new Date(now);
  if (value.unit === 'day') start.setDate(start.getDate() - value.amount);
  else if (value.unit === 'week') start.setDate(start.getDate() - value.amount * 7);
  else if (value.unit === 'month') start.setMonth(start.getMonth() - value.amount);
  else if (value.unit === 'year') start.setFullYear(start.getFullYear() - value.amount);
  return { startDate: start.toISOString().slice(0, 10), endDate: end };
}

export const AnalyticsDashboard: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse | null>(null);
  const [modelConfigs, setModelConfigs] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTestId, setSelectedTestId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
  const [visibleModelIds, setVisibleModelIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsResult, modelsResult] = await Promise.all([
        apiService.getAnalytics(
          ...Object.values(resolveDateRange(dateRange)) as [string?, string?]
        ),
        !initialized ? apiService.getModels() : Promise.resolve(null),
      ]);
      setAnalyticsData(analyticsResult);
      if (modelsResult) {
        setModelConfigs(modelsResult.models);
        // Default: show enabled models only
        const enabledIds = new Set(
          modelsResult.models.filter((m) => m.enabled).map((m) => m.modelId)
        );
        setVisibleModelIds(enabledIds);
        setInitialized(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, initialized]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const toggleModel = (modelId: string) => {
    setVisibleModelIds((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });
  };

  const getAllModels = (): Record<string, ModelMetricsAggregated> => {
    if (!analyticsData) return {};
    if (selectedTestId === 'all') return analyticsData.overall.models;
    return analyticsData.testScenarios[selectedTestId]?.models ?? {};
  };

  const allModels = getAllModels();

  // Filter models by visibility
  const models = useMemo(() => {
    const filtered: Record<string, ModelMetricsAggregated> = {};
    for (const [id, m] of Object.entries(allModels)) {
      if (visibleModelIds.has(id)) filtered[id] = m;
    }
    return filtered;
  }, [allModels, visibleModelIds]);

  const getFilteredRatings = (): Record<string, Record<string, number>> => {
    if (!analyticsData) return {};
    const raw = selectedTestId === 'all'
      ? analyticsData.overall.ratings
      : analyticsData.testScenarios[selectedTestId]?.ratings ?? {};
    // Filter ratings to only visible models
    const filtered: Record<string, Record<string, number>> = {};
    for (const [rater, rated] of Object.entries(raw)) {
      if (!visibleModelIds.has(rater)) continue;
      const inner: Record<string, number> = {};
      for (const [ratedId, val] of Object.entries(rated)) {
        if (visibleModelIds.has(ratedId)) inner[ratedId] = val;
      }
      if (Object.keys(inner).length > 0) filtered[rater] = inner;
    }
    return filtered;
  };

  const getTotalRuns = (): number => {
    if (!analyticsData) return 0;
    if (selectedTestId === 'all') return analyticsData.overall.totalRuns;
    return analyticsData.testScenarios[selectedTestId]?.totalRuns ?? 0;
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>Loading analytics data...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        header="Failed to load analytics"
        action={<Button onClick={fetchAnalytics}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }

  // Build toggle list: all models from analytics + any config-only models
  const allModelIds = Array.from(new Set([
    ...Object.keys(allModels),
    ...modelConfigs.map((c) => c.modelId),
  ]));
  const modelDisplayNames: Record<string, string> = {};
  for (const id of allModelIds) {
    const fromAnalytics = allModels[id];
    const fromConfig = modelConfigs.find((c) => c.modelId === id);
    modelDisplayNames[id] = fromAnalytics?.displayName ?? fromConfig?.displayName ?? id;
  }

  const ratings = getFilteredRatings();
  const totalRuns = getTotalRuns();
  const selectedOption = scenarioOptions.find((o) => o.value === selectedTestId) ?? scenarioOptions[0];

  const modelOptions: MultiselectProps.Option[] = allModelIds.sort().map((id) => ({
    label: modelDisplayNames[id],
    value: id,
  }));
  const selectedModelOptions = modelOptions.filter((o) => visibleModelIds.has(o.value!));

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        actions={
          <SpaceBetween direction="horizontal" size="s">
            <Multiselect
              selectedOptions={selectedModelOptions}
              onChange={({ detail }) => {
                setVisibleModelIds(new Set(detail.selectedOptions.map((o) => o.value!)));
              }}
              options={modelOptions}
              placeholder="Filter models"
              tokenLimit={2}
            />
            <DateRangePicker
              value={dateRange}
              onChange={({ detail }) => setDateRange(detail.value)}
              relativeOptions={RELATIVE_OPTIONS}
              placeholder="All time"
              i18nStrings={{
                todayAriaLabel: 'Today',
                nextMonthAriaLabel: 'Next month',
                previousMonthAriaLabel: 'Previous month',
                customRelativeRangeDurationLabel: 'Duration',
                customRelativeRangeDurationPlaceholder: 'Enter duration',
                customRelativeRangeOptionLabel: 'Custom range',
                customRelativeRangeOptionDescription: 'Set a custom range in the past',
                customRelativeRangeUnitLabel: 'Unit of time',
                formatRelativeRange: (e) => {
                  const unit = e.amount === 1 ? e.unit : `${e.unit}s`;
                  return `Last ${e.amount} ${unit}`;
                },
                formatUnit: (unit, value) => (value === 1 ? unit : `${unit}s`),
                relativeModeTitle: 'Relative range',
                absoluteModeTitle: 'Absolute range',
                relativeRangeSelectionHeading: 'Choose a range',
                startDateLabel: 'Start date',
                startTimeLabel: 'Start time',
                endDateLabel: 'End date',
                endTimeLabel: 'End time',
                clearButtonLabel: 'Clear',
                cancelButtonLabel: 'Cancel',
                applyButtonLabel: 'Apply',
              }}
              isValidRange={(value) => {
                if (value?.type === 'absolute') {
                  if (!value.startDate || !value.endDate) {
                    return { valid: false, errorMessage: 'Select a complete date range' };
                  }
                  if (new Date(value.startDate) > new Date(value.endDate)) {
                    return { valid: false, errorMessage: 'Start date must be before end date' };
                  }
                }
                return { valid: true };
              }}
              dateOnly
            />
            <Select
              selectedOption={selectedOption}
              onChange={({ detail }) => setSelectedTestId(detail.selectedOption.value ?? 'all')}
              options={scenarioOptions}
              placeholder="Select test scenario"
            />
          </SpaceBetween>
        }
      >
        Analytics Dashboard
      </Header>

      <Container header={<Header variant="h3">Summary</Header>}>
        <SummaryCards models={models} totalRuns={totalRuns} />
      </Container>

      <Container header={<Header variant="h3">Token Usage</Header>}>
        <TokenUsageChart models={models} />
      </Container>

      <Container header={<Header variant="h3">Response Time</Header>}>
        <ResponseTimeChart models={models} />
      </Container>

      <Container header={<Header variant="h3">Rating Matrix</Header>}>
        <RatingHeatmap ratings={ratings} models={models} />
      </Container>

      <Container header={<Header variant="h3">Success Rate</Header>}>
        <SuccessRateChart models={models} />
      </Container>
    </SpaceBetween>
  );
};
